import * as React from "react";
import { Building2, Compass, FileText, Layers, ShieldCheck, Trees, House as Home, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function BasementGuide() {
  return <section id="decaturbasementremodelingguide" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Decorative subtle background accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/80 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold tracking-wide border-border bg-card/60 backdrop-blur-xs">
            Decatur Homeowner Guide
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight text-foreground">
            Basement Remodeling in Decatur: Foundations, Codes, and Historic Standards
          </h2>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            Converting a lower level in Decatur involves navigating century-old masonry, local stormwater ordinances, and strict preservation guidelines. Here is an architectural and regulatory overview of renovating lower levels in our neighborhoods.
          </p>
        </div>

        {/* Section 1: Foundation & Architectural Realities */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-secondary text-foreground">
              <Home className="size-5" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Architectural Typologies</span>
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                Foundation Conditions in Bungalows, Craftsman, and Brick Ranch Homes
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-card/80 backdrop-blur-xs border-border/70 shadow-sm">
              <CardHeader>
                <Badge variant="secondary" className="w-fit text-xs font-medium">1910s–1930s</Badge>
                <CardTitle className="font-serif text-xl mt-2">Historic Craftsman & Bungalows</CardTitle>
                <CardDescription>Oakhurst, MAK District & Winnona Park</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                <p>
                  Early 20th-century Decatur bungalows commonly sit on perimeter brick piers with infill masonry or unreinforced rubble granite walls. Many original lower levels were designed as unfinished service cellars for coal furnaces.
                </p>
                <p>
                  Common realities include ceiling heights under seven feet, uneven natural slab footings, and moisture penetration from porous lime-based mortar resting in dense Georgia red clay.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/80 backdrop-blur-xs border-border/70 shadow-sm">
              <CardHeader>
                <Badge variant="secondary" className="w-fit text-xs font-medium">1940s–1950s</Badge>
                <CardTitle className="font-serif text-xl mt-2">Post-War Cottages & Colonials</CardTitle>
                <CardDescription>Great Lakes & Chelsea Heights</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                <p>
                  These structures often feature early concrete masonry unit (CMU) block foundations or cast-in-place walls. While head height improves over older bungalows, structural support columns often segment the open floor plan.
                </p>
                <p>
                  Plumbing tie-ins frequently require sub-slab trenching through uninsulated concrete slabs to connect with municipal sewer lines along residential street easements.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/80 backdrop-blur-xs border-border/70 shadow-sm">
              <CardHeader>
                <Badge variant="secondary" className="w-fit text-xs font-medium">1950s–1960s</Badge>
                <CardTitle className="font-serif text-xl mt-2">Mid-Century Brick Ranches</CardTitle>
                <CardDescription>Midway Woods & Clairmont Heights</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                <p>
                  Ranch styles frequently utilize daylight or walk-out basement configurations along sloped topography. Full 8-foot head clearances make these candidates for whole-floor living spaces and guest suites.
                </p>
                <p>
                  Renovations here focus on thermal envelope upgrades, perimeter drain tile installations, sump pump integrations, and modern subfloor moisture barriers before framing.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-secondary/40 border border-border/60 rounded-xl p-6">
            <h4 className="font-serif text-base font-semibold text-foreground mb-2 flex items-center gap-2">
              <Layers className="size-4 text-foreground/80" />
              Structural Underpinning and Slab Lowering Considerations
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              When standard finished ceiling height (IRC minimum 7 feet, with 6 feet 8 inches under beams and ducts) is not met, lowering the basement floor requires precise sectional benching or sequential structural underpinning beneath original footings. Because Decatur sits on cohesive clay soils that expand and contract with rainfall patterns, hydrostatic relief systems and interior perimeter drainage channels must be designed before pouring new reinforced concrete slabs.
            </p>
          </div>
        </div>

        <Separator className="my-14" />

        {/* Section 2: Permitting Jurisdictions */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-secondary text-foreground">
              <FileText className="size-5" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Regulatory Framework</span>
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                City of Decatur vs. Unincorporated DeKalb County Permitting
              </h3>
            </div>
          </div>

          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
            Property owners frequently misinterpret boundary jurisdictions around Decatur. Properties inside the 4.4-square-mile incorporated <strong>City of Decatur</strong> municipal limits are governed by the Decatur Design, Environment &amp; Construction Division, whereas adjacent zip codes (such as 30030, 30032, and 30033 in <strong>Unincorporated DeKalb County</strong>) follow DeKalb County Department of Planning and Sustainability procedures.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* City of Decatur Card */}
            <Card className="bg-card border-border/80 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl">City of Decatur Jurisdiction</CardTitle>
                  <Badge variant="outline" className="text-xs font-semibold">City Limits</Badge>
                </div>
                <CardDescription>Unified Development Ordinance (UDO) Standards</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Impervious Lot Coverage:</span>
                    <p className="mt-0.5">Strict lot coverage caps (typically 40% in single-family residential districts). Exterior basement stairwells or areaway modifications must account for surface runoff calculations.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Tree Canopy Protection:</span>
                    <p className="mt-0.5">Decatur Tree Ordinance requires root zone protection plans for exterior excavations, egress wells, or underground drainage trenches within critical root zones (CRZ) of protected canopy trees.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Plan Review &amp; Inspections:</span>
                    <p className="mt-0.5">Comprehensive combined building, trade (electrical, mechanical, plumbing), and energy code documentation reviewed at City Hall on Church Street.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DeKalb County Card */}
            <Card className="bg-card border-border/80 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl">Unincorporated DeKalb County</CardTitle>
                  <Badge variant="secondary" className="text-xs font-semibold">County System</Badge>
                </div>
                <CardDescription>DeKalb Planning &amp; Sustainability Department</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Zoning Setback Compliance:</span>
                    <p className="mt-0.5">Side and rear setback allowances follow standard county residential zone classifications (R-75, R-85, R-100), affecting where basement window walkouts or exterior stair runs can be positioned.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">Sewer Capacity &amp; Plumbing Review:</span>
                    <p className="mt-0.5">Basement additions introducing new full bathrooms or wet bars are processed through the DeKalb County Watershed Management review for sanitary fixture loads.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-4 text-foreground shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-foreground">County Trade Submittals:</span>
                    <p className="mt-0.5">Permits are routed through the county's digital permitting portal with separate inspections conducted for framing, rough MEPs, insulation, and final certificate of occupancy.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-14" />

        {/* Section 3: Historic District Design Review */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-secondary text-foreground">
              <Compass className="size-5" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Preservation &amp; Exterior Work</span>
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                Historic District Design Review: Egress Windows, Areaways, and Exterior Changes
              </h3>
            </div>
          </div>

          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed mb-8">
            <p>
              When a basement remodeling project incorporates finished bedrooms, home offices, or separate accessory dwelling suites, the Georgia International Residential Code (IRC) mandates emergency escape and rescue openings (egress windows or exterior doors). In Decatur's designated local historic districts—such as <strong>McDonough-Adams-Kings (MAK)</strong>, <strong>Ponce de Leon Court</strong>, and <strong>Old Decatur</strong>—any visible exterior alteration requires a <strong>Certificate of Appropriateness (COA)</strong> from the Decatur Historic Preservation Commission (HPC).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-card/90 border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-foreground flex items-center gap-2">
                  <Sparkles className="size-4 text-foreground/80" />
                  Egress Window Wells &amp; Fenestration
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                <p>
                  <strong>Visibility Standards:</strong> Egress openings must be placed on rear or secondary side facades whenever possible. Wells located within view of the public right-of-way must minimize visual disruption to the original historic foundation line.
                </p>
                <p>
                  <strong>Window Profile &amp; Sashes:</strong> Replacement or newly cut basement windows should maintain proportions and materials compatible with the historic structure, avoiding modern unpainted metal finishes or non-traditional muntin profiles.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/90 border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-foreground flex items-center gap-2">
                  <Building2 className="size-4 text-foreground/80" />
                  Exterior Stairs, Areaways &amp; Mechanicals
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3 leading-relaxed">
                <p>
                  <strong>Walk-Out Stairwells:</strong> Excavating new below-grade walk-outs requires retaining walls constructed from materials consistent with historic character—such as parged masonry, matching modular brick, or screened stone.
                </p>
                <p>
                  <strong>Vents &amp; Mechanical Wall Penetrations:</strong> Modern basement HVAC units, direct-vent mechanical exhausts, and sub-slab radon mitigation discharge pipes must be located discreetly on non-primary elevations and shielded from public street view.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Planning Checklist Banner */}
        <div className="bg-card border border-border rounded-xl p-8 shadow-sm">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <h3 className="font-serif text-2xl font-semibold text-foreground">
                Planning a Lower-Level Transformation in Decatur?
              </h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Review your neighborhood zoning, foundation specifications, and architectural documentation before commencing structural alterations or permitting submissions.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="shadow-xs">
                <Link to="/ProcessPlanningPage">
                  View Planning Process
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ContactPage">
                  Consultation Inquiries
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}